/* eslint-disable no-unused-vars */
/* React */
import React, { useEffect, useState, useContext } from 'react';

/* Translation */
import { useTranslation } from 'react-i18next';

/* Router */
import { useRouter } from 'routes/index';

/* Components */
import { Modal } from 'lego-dist';
import ClassroomList from './ClassroomList';

/* Styled Components */
import {
  CenterTab,
  ClassroomHeader,
  ClassroomCount,
  ClassroomActions,
  SearchBar,
  SearchIcon,
  AddClassroomBtnMobile,
  AddClassroomBtn,
} from './styles/CenterGeneral';

/* Views */
import AddClassroomView from 'views/AddClassroomView';
import AddClassroomSuccess from 'views/AddClassroomView/SuccessView';

/* Services, Hooks, Constants */
import { debounce } from 'lib/utils';
import { AppContext } from 'App';
import { APP_SECTIONS_PATHS } from 'constants/routes';
import { KEYS_LOCALSTORAGE } from 'constants/general';
import { EventProviders, AppEvents } from 'constants/events';
import GroupService from 'services/GroupService';
import EventReporter from 'appEvents';
import useGroups from 'hooks/groups/UseGroups';
import { isRoleType } from 'lib/helpers';
import { ROLES_OBJECT } from 'constants/onBoarding';
import RendererIf from 'components/RendererIf';
import useCurrentUser from 'hooks/useCurrentUser';
import { ActionPermission } from 'lib/specialPermissions';
import themes from 'themes';

interface CenterGeneralProps {
  center: any;
  classrooms: any;
  coreClassrooms: any;
  refreshData: any;
}

const groupService = GroupService.getInstance();
const isCustomizeProductSent = localStorage.getItem(
  KEYS_LOCALSTORAGE.CUSTOMIZE_PROD_SENT,
);

const CenterGeneral: React.FC<CenterGeneralProps> = ({
  center,
  classrooms,
  coreClassrooms,
  refreshData,
}) => {
  const { t: translate } = useTranslation();
  const { history, navigateTo, currentParamsRef } = useRouter();
  const { isFreemiumUser, isDemoUser } = useContext(AppContext);
  const [filteredClassrooms, setFilteredClassrooms] = useState<any[]>([]);
  const { can } = useCurrentUser();
  useEffect(() => setFilteredClassrooms(classrooms), [classrooms]);

  /* open modals */
  const [openAddClassroomModal, setOpenAddClassroomModal] = useState(false);
  const [openAddClassroomSuccess, setOpenAddClassroomSuccess] = useState(false);

  /* ADD CLASSROOM functions */
  const { setActiveGroupById }: any = useGroups();
  const [, setNewCenter] = useState<any>({ data: {}, index: 0 });
  const [newClassroom, setNewClassroom] = useState<any>({ data: {}, index: 0 });

  const {
    current: { centerId },
  } = currentParamsRef;

  const handleAddClassroom = () => {
    if (isFreemiumUser || isDemoUser) {
      history.push(APP_SECTIONS_PATHS.PREMIUM_PROCESS);
    } else {
      setOpenAddClassroomModal(true);
    }
  };

  const handleSubmitClassroom = async (res: any) => {
    const payload = {
      group: {
        name: res.name,
        min: 1,
        max: 72,
        ...res.avatar,
      },
    };

    const { createdGroup, updatedGroupList } = await groupService.createGroup(
      center?.id,
      payload,
    );

    setActiveGroupById(createdGroup?.id, updatedGroupList);
    if (!isRoleType([ROLES_OBJECT.TEACHER.value])) {
      setFilteredClassrooms(updatedGroupList);
    }
    setOpenAddClassroomModal(false);

    EventReporter.view(AppEvents.ADD_CLASS_ROOM({ source: 'settings' }), [
      EventProviders.MIXPANEL,
    ]);
    if (!isCustomizeProductSent) {
      EventReporter.view(AppEvents.CUSTOMIZE_PRODUCT(), [
        EventProviders.FB_PIXEL,
        EventProviders.GTM,
      ]);
      localStorage.setItem(KEYS_LOCALSTORAGE.CUSTOMIZE_PROD_SENT, 'true');
    }

    setOpenAddClassroomSuccess(true);

    navigateTo(APP_SECTIONS_PATHS.CLASSROOM_DETAILS, {
      centerId,
      groupId: createdGroup?.id,
      centerName: createdGroup?.name || `${translate('settings:CENTER_SECTION.CENTER')}`,
      prev: 'center',
      externalIdValue: createdGroup?.external_id,
    });
  };

  /* SEARCH functions */
  const handleSearch = debounce((value: any) => {
    const pattern = new RegExp(`${value}`, 'i');
    const searchedClassrooms = classrooms.filter(
      (classroom: any) => classroom.name && pattern.test(classroom.name),
    );
    setFilteredClassrooms(searchedClassrooms);
  }, 300);

  return (
    <CenterTab>
      <ClassroomHeader>
        <ClassroomCount>
          {translate('settings:CLASSROOMS')}
          {`(${classrooms.length})`}
        </ClassroomCount>
        <ClassroomActions>
          <SearchBar
            theme="Rounded"
            placeholder={translate('settings:STAFF_SECTION.MANAGE.SEARCH')}
            onChange={handleSearch}
            rightSlot={() => <SearchIcon />}
          />
          <RendererIf condition={can(ActionPermission.ADD_CLASSROOMS)}>
            <>
              <AddClassroomBtnMobile
                rounded
                flat
                fill
                color="#75B753"
                onClick={() => handleAddClassroom()}
              >
                +
              </AddClassroomBtnMobile>
              <AddClassroomBtn
                rounded
                flat
                fill
                color="#75B753"
                onClick={() => handleAddClassroom()}
              >
                {translate('settings:ADD_CLASSROOM')}
              </AddClassroomBtn>
            </>
          </RendererIf>
        </ClassroomActions>
      </ClassroomHeader>
      <ClassroomList
        classrooms={
          filteredClassrooms.length > 0 ? filteredClassrooms : classrooms
        }
        coreClassrooms={coreClassrooms}
        handleOnDeleteClassroom={refreshData}
      />
      <Modal
        openModal={openAddClassroomModal}
        title={translate('settings:ADD_CLASSROOM')}
        headerColor={themes.colors.newKinedu}
        closeButtonColor="#ffffff"
        confirmCloseModal
        confirmCloseModalTexts={{
          title: translate('activities:IF_YOU_LEAVE.TITLE'),
          button_leave: translate('activities:IF_YOU_LEAVE.BUTTON_LEAVE'),
          button_stay: translate('activities:IF_YOU_LEAVE.BUTTON_STAY'),
          description: translate('activities:IF_YOU_LEAVE.DESCRIPTION'),
          warning: translate('activities:IF_YOU_LEAVE.WARNING'),
        }}
        persistent
        onClosedModal={() => {
          setOpenAddClassroomModal(false);
          setNewClassroom({ data: undefined, index: 0 });
          setNewCenter({ data: undefined, index: 0 });
        }}
      >
        {() => (
          <AddClassroomView
            classroom={newClassroom.data}
            onSubmit={handleSubmitClassroom}
          />
        )}
      </Modal>
      <Modal
        openModal={openAddClassroomSuccess}
        onClosedModal={() => {
          setOpenAddClassroomSuccess(false);
        }}
      >
        {() => <AddClassroomSuccess />}
      </Modal>
    </CenterTab>
  );
};

export default CenterGeneral;
